import { MkIcon } from "@/mk/MkIcon";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { recordRoute } from "@/app/navigation";
import { api } from "@/api/client";
import { useBusSocket } from "@/app/useBusSocket";
import { useToast } from "@/app/toast";

import { MkEmptyState } from "./MkEmptyState";
import { MkSkeleton } from "./MkSkeleton";
import type { FabButton } from "./types";

type Box = "pending" | "notifications" | "messages" | "activities";

interface LatestAlert {
    id: number;
    title: string;
    severity: "critical" | "warning" | "info";
    model: string;
    res_id: number;
}

interface Counts {
    pending: number;
    notifications: number;
    messages: number;
    activities: number;
    latest_alert?: LatestAlert | false;
}

const SEEN_ALERT_KEY = "pm.inbox.seenAlert";

const ALERT_LOOK: Record<string, { icon: string; tone: string }> = {
    critical: { icon: "fa-exclamation-triangle", tone: "danger" },
    warning: { icon: "fa-bell", tone: "warning" },
    info: { icon: "fa-info-circle", tone: "info" },
};

function seenAlert(): number {
    try {
        return Number(window.localStorage.getItem(SEEN_ALERT_KEY)) || 0;
    } catch {
        return 0;
    }
}

function markAlertSeen(id: number) {
    try {
        window.localStorage.setItem(SEEN_ALERT_KEY, String(id));
    } catch {
        return;
    }
}

interface InboxItem {
    id: number | string;
    title: string;
    subtitle?: string;
    preview?: string;
    badge?: number;
    model?: string;
    res_id?: number;
    icon?: string;
    tone?: string;
    late?: boolean;
    alertId?: number;
    alertState?: string;
    isRead?: boolean;
}

interface RawNotification {
    id: number | string; subject: string; record_name: string; author: string; preview: string;
    model: string; res_id: number; kind?: "alert" | "email"; severity?: string;
    alert_id?: number; state?: string; is_read?: boolean;
}

interface RawMessage {
    id: number; channel: string; channel_id: number; unread: number;
}

interface RawActivity {
    id: number; summary: string; activity_type: string; res_name: string;
    model: string; res_id: number;
}

interface RawPending {
    key: string; model: string; res_id: number; title: string; project: string;
    group: string; action: string; icon: string; tone: string; late: boolean; date: string;
}

const BOXES: Box[] = ["pending", "notifications", "messages", "activities"];

export function MkFabMenu({ extra = [], openRecord }: {
    extra?: FabButton[];
    /** where a record of this model lives in the application, if anywhere */
    openRecord?(model: string, id: number): void;
}) {
    const { t } = useTranslation();
    const queries = useQueryClient();
    const navigate = useNavigate();
    const location = useLocation();
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const kind = params.get("pm_inbox") as Box;
        if (!BOXES.includes(kind)) return;
        const model = params.get("pm_model");
        const id = Number(params.get("pm_id"));
        const target = model === "discuss.channel" && id > 0 ? `/discuss?channel=${id}`
            : model && id > 0 ? recordRoute(model, id, undefined, true) : null;
        if (target) navigate(target, { replace: true });
        else {
            setPanel(kind);
            for (const key of ["pm_inbox", "pm_model", "pm_id"]) params.delete(key);
            navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
        }
    }, [location.search]);
    const toast = useToast();
    const [panel, setPanel] = useState<string | null>(null);
    // A live bus toast already told the user about a fresh alert; the polled
    // latest-alert fallback stays quiet for a moment so it does not repeat it.
    const liveToastUntil = useRef(0);

    const counts = useQuery<{ data: Counts }>({
        queryKey: ["inbox", "counts"],
        queryFn: () => api.raw<Counts>("/inbox"),
        staleTime: 30_000,
        refetchInterval: 60_000,
        refetchIntervalInBackground: true,
        refetchOnWindowFocus: true,
    });

    useBusSocket((event) => {
        if (event.type === "pm.inbox.refresh") {
            queries.invalidateQueries({ queryKey: ["inbox"] });
            return;
        }
        if (event.type === "discuss.channel/new_message") {
            queries.invalidateQueries({ queryKey: ["inbox"] });
            return;
        }
        if (event.type !== "simple_notification") {
            return;
        }
        const payload = event.payload as {
            title?: string; message?: string; type?: string; sticky?: boolean;
        };
        queries.invalidateQueries({ queryKey: ["inbox", "counts"] });
        if (!payload.title) {
            return;
        }
        liveToastUntil.current = Date.now() + 120_000;
        const severity = String(payload.type || "info");
        const show = severity === "danger" || severity === "critical" ? toast.danger
            : severity === "warning" ? toast.warning
                : severity === "success" ? toast.success : toast.info;
        show(payload.title, {
            title: payload.message || undefined,
            icon: ALERT_LOOK[severity]?.icon,
            sticky: payload.sticky,
        });
    });

    const latest = counts.data?.data.latest_alert;
    useEffect(() => {
        if (!latest || latest.id <= seenAlert()) {
            return;
        }
        markAlertSeen(latest.id);
        if (Date.now() < liveToastUntil.current) {
            return;
        }
        const show = latest.severity === "critical" ? toast.danger
            : latest.severity === "warning" ? toast.warning : toast.info;
        show(latest.title, {
            title: t("mk.inbox.newAlert"),
            icon: ALERT_LOOK[latest.severity]?.icon,
            sticky: latest.severity === "critical",
            actions: [{
                label: t("mk.inbox.openAlerts"),
                onClick: () => setPanel("notifications"),
            }],
        });
    }, [latest ? latest.id : 0]);

    const box = BOXES.includes(panel as Box) ? (panel as Box) : null;

    const list = useQuery<InboxItem[]>({
        queryKey: ["inbox", box],
        enabled: box !== null,
        staleTime: 0,
        queryFn: async () => {
            if (box === "pending") {
                const payload = await api.raw<RawPending[]>("/inbox/pending");
                return payload.data.map((row, index) => ({
                    id: index,
                    title: row.title || row.group,
                    subtitle: [row.action, row.project].filter(Boolean).join(" · "),
                    preview: row.group,
                    model: row.model,
                    res_id: row.res_id,
                    icon: row.icon,
                    tone: row.tone,
                    late: row.late,
                }));
            }
            if (box === "notifications") {
                const payload = await api.raw<RawNotification[]>("/inbox/notifications");
                return payload.data.map((row) => ({
                    id: row.id,
                    title: row.subject || row.record_name || t("mk.inbox.notification"),
                    subtitle: row.author || t("mk.inbox.system"),
                    preview: row.preview,
                    model: row.model,
                    res_id: row.res_id,
                    icon: row.kind === "email" ? "fa-envelope" : row.kind === "alert" ? ALERT_LOOK[row.severity || "info"]?.icon : undefined,
                    tone: row.kind === "alert" ? ALERT_LOOK[row.severity || "info"]?.tone : undefined,
                    late: row.kind === "alert" && row.severity === "critical",
                    alertId: row.kind === "alert" ? row.alert_id : undefined,
                    alertState: row.kind === "alert" ? row.state : undefined,
                    isRead: row.kind === "alert" ? Boolean(row.is_read) : undefined,
                }));
            }
            if (box === "messages") {
                const payload = await api.raw<RawMessage[]>("/inbox/messages");
                return payload.data.map((row) => ({
                    id: row.id,
                    title: row.channel || t("mk.inbox.channel"),
                    subtitle: `${row.unread || 0} ${t("mk.inbox.unread")}`,
                    badge: row.unread || 0,
                    model: "discuss.channel",
                    res_id: row.channel_id,
                }));
            }
            const payload = await api.raw<RawActivity[]>("/inbox/activities");
            return payload.data.map((row) => ({
                id: row.id,
                title: row.summary || row.activity_type || t("mk.inbox.activity"),
                subtitle: row.res_name || "",
                model: row.model,
                res_id: row.res_id,
            }));
        },
    });

    useEffect(() => {
        if (!panel) {
            return;
        }
        function onKey(event: KeyboardEvent) {
            if (event.key === "Escape") {
                setPanel(null);
            }
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [panel]);

    const data = counts.data?.data;
    const buttons: FabButton[] = [
        { id: "pending", icon: "fa-hourglass-half", label: t("mk.inbox.pending"),
          variant: "warning", badge: data?.pending ?? 0 },
        { id: "notifications", icon: "fa-bell", label: t("mk.inbox.notifications"),
          variant: "primary", badge: data?.notifications ?? 0 },
        { id: "messages", icon: "fa-comments", label: t("mk.inbox.messages"),
          variant: "info", badge: data?.messages ?? 0 },
        { id: "activities", icon: "fa-clock-o", label: t("mk.inbox.activities"),
          variant: "warning", badge: data?.activities ?? 0 },
        ...extra,
    ];

    const current = buttons.find((button) => button.id === panel);

    function openPanel(button: FabButton) {
        if (button.onClick) {
            button.onClick();
            setPanel(null);
            return;
        }
        setPanel((open) => (open === button.id ? null : button.id));
    }

    async function alertAction(item: InboxItem, action: "read" | "acknowledge" | "mute" | "close") {
        if (!item.alertId) {
            return;
        }
        try {
            await api.act("alerts", item.alertId, action, action === "mute" ? { days: 7 } : {});
            queries.invalidateQueries({ queryKey: ["inbox"] });
        } catch (error) {
            toast.danger((error as Error).message);
        }
    }

    function openItem(item: InboxItem) {
        if (!item.model || !item.res_id) {
            return;
        }
        if (item.alertId) {
            void alertAction(item, "read");
        }
        setPanel(null);
        if (item.model === "discuss.channel") {
            navigate(`/discuss?channel=${item.res_id}`);
            return;
        }
        openRecord?.(item.model, item.res_id);
    }

    const items = list.data ?? [];

    return (
        <div className="mk-fabmenu">
            {panel && <div className="mk-fabmenu-scrim" onClick={() => setPanel(null)} />}

            {panel && box && (
                <div className="mk-fabmenu-panel">
                    <div className="mk-fabmenu-panel-head">
                        <span className="mk-fabmenu-panel-title">{current?.label}</span>
                        <button className="mk-fabmenu-panel-close" onClick={() => setPanel(null)}
                                aria-label={t("mk.close")}>
                            <MkIcon name="fa-times" />
                        </button>
                    </div>
                    <div className="mk-fabmenu-panel-body">
                        {list.isLoading ? (
                            <div className="mk-inbox-loading">
                                <MkSkeleton lines={6} />
                            </div>
                        ) : !items.length ? (
                            <MkEmptyState icon="fa-check-circle" title={t("mk.inbox.caughtUp")} />
                        ) : items.map((item) => (
                            <div key={item.id} className="mk-inbox-row">
                                <button
                                    className={`mk-inbox-item${item.late ? " mk-inbox-item-late" : ""}`}
                                    onClick={() => openItem(item)}>
                                    {item.icon && (
                                        <span className={`mk-inbox-item-icon mk-inbox-item-${item.tone || "info"}`}>
                                            <MkIcon name={item.icon} />
                                        </span>
                                    )}
                                    <div className="mk-inbox-item-main">
                                        <div className="mk-inbox-item-title">{item.title}</div>
                                        {item.subtitle && (
                                            <div className="mk-inbox-item-sub">{item.subtitle}</div>
                                        )}
                                        {item.preview && (
                                            <div className="mk-inbox-item-preview">{item.preview}</div>
                                        )}
                                    </div>
                                    {item.badge ? (
                                        <span className="mk-inbox-item-badge">{item.badge}</span>
                                    ) : null}
                                    <MkIcon name="fa-angle-right" className="mk-inbox-item-go" />
                                </button>
                                {item.alertId && (
                                    <div className="mk-inbox-actions">
                                        {!item.isRead && (
                                            <button type="button" title={t("alerts.markRead")}
                                                    aria-label={t("alerts.markRead")}
                                                    onClick={() => alertAction(item, "read")}>
                                                <MkIcon name="fa-envelope-open-o" />
                                            </button>
                                        )}
                                        <button type="button" title={t("alerts.acknowledge")}
                                                aria-label={t("alerts.acknowledge")}
                                                onClick={() => alertAction(item, "acknowledge")}>
                                            <MkIcon name="fa-check" />
                                        </button>
                                        <button type="button" title={t("alerts.mute")}
                                                aria-label={t("alerts.mute")}
                                                onClick={() => alertAction(item, "mute")}>
                                            <MkIcon name="fa-bell-slash" />
                                        </button>
                                        <button type="button" title={t("alerts.close")}
                                                aria-label={t("alerts.close")}
                                                onClick={() => alertAction(item, "close")}>
                                            <MkIcon name="fa-times" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="mk-fabmenu-bar">
                {buttons.map((button) => (
                    <button key={button.id}
                            className={`mk-fabmenu-btn${panel === button.id ? " mk-fabmenu-btn-active" : ""}`}
                            title={button.label} aria-label={button.label}
                            aria-expanded={panel === button.id}
                            onClick={() => openPanel(button)}>
                        <MkIcon name={button.icon} />
                        {button.badge && button.badge > 0 ? (
                            <span className={`mk-fabmenu-badge mk-fabmenu-badge-${button.variant || "primary"}`}>
                                {button.badge > 99 ? "99+" : button.badge}
                            </span>
                        ) : null}
                    </button>
                ))}
            </div>
        </div>
    );
}
